import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, ReactNode, useCallback, useState } from "react";
import useExpandAndCollapse from "../hooks/useExpandAndCollapse";
import { DataGridContext } from "./DataGridContext";
import { useGridRootProps } from "../hooks/useGridRootProps";
import { DataGridProps } from "../models/dataGridProps";
import useLocalStorage from "@hooks/useLocalStorage";

type DataGridContextProviderProps = {
  apiRef: MutableRefObject<GridApiPremium> | undefined;
  rootProps?: DataGridProps;
  children: ReactNode;
};

export interface GridOptions {
  headerFilters: boolean;
  autosizeColumns: boolean;
  autosizeTreeDataGroupingColumns: boolean;
}

export const DataGridContextProvider = ({ apiRef, rootProps, children }: DataGridContextProviderProps) => {
  const { onExpandAll, onCollapseAll, onExpandRow, onCollapseRow, expandAllDisabled, collapseAllDisabled } = useExpandAndCollapse(apiRef as MutableRefObject<GridApiPremium>);
  const storageKey = rootProps?.storageKey ? `${rootProps?.storageKey}-options` : undefined;
  const [gridState, setGridState] = useLocalStorage<GridOptions>(storageKey, {
    headerFilters: false,
    autosizeColumns: rootProps?.autosizeColumns ?? false,
    autosizeTreeDataGroupingColumns: rootProps?.autosizeTreeDataGroupingColumns ?? false,
  });

  const setHeaderFilters = useCallback((value: boolean) => {
    setGridState('headerFilters', value);
  }, [gridState, setGridState]);
  const setAutosizeColumns = useCallback((value: boolean) => {
    setGridState('autosizeColumns', value );
  }, [gridState, setGridState]);
  const setAutosizeTreeDataGroupingColumns = useCallback((value: boolean) => {
    setGridState('autosizeTreeDataGroupingColumns',  value);
  }, [gridState, setGridState]);

  return (
    <DataGridContext.Provider
      value={{
        headerFilters: gridState.headerFilters,
        autosizeColumns: !!rootProps?.autosizeColumns && gridState.autosizeColumns,
        autosizeTreeDataGroupingColumns: !!rootProps?.autosizeTreeDataGroupingColumns && gridState.autosizeTreeDataGroupingColumns,
        setHeaderFilters,
        setAutosizeColumns,
        setAutosizeTreeDataGroupingColumns,
        onExpandAll,
        onCollapseAll,
        onExpandRow,
        onCollapseRow,
        expandAllDisabled,
        collapseAllDisabled,
      }}
    >
      {children}
    </DataGridContext.Provider>
  );
};
